import { useEffect, useState } from 'react';

import { Select, Stack, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { useLazyListAppointmentsQuery, useUpdateAppointmentMutation } from '@/store/slices/api/appointmentApiSlice';
import { patientsApiSlice } from '@/store/slices/api/patientsApiSlice';
import { useCancelTaskMutation, useLazyListTasksQuery } from '@/store/slices/api/tasksApiSlice';
import { useUpdateDeviceMutation } from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';
import { useUpdatePatientMutation } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';
import { useAppDispatch } from '@/store/hooks';
import { INACTIVE_STATUS_REASONS } from '@/pages/physician/TransmissionReportPage/ReportPatientDetails/DetailsSection/PatientStatusModal/constants';
import { MatchDevice } from '@/types/matchDevice';
import { Patient } from '@/types/patient';
import { TaskResponseDto } from '@/types/task';
import { API_SLICE } from '@/utils/constants/apiEndpoints';
import CustomModal from '@/ui/Modal';

interface DeactivatePatientModalProps {
  opened: boolean;
  onClose: () => void;
  patientId: string;
  patientName: string;
  devices?: MatchDevice[];
}

const REASON_LABELS: Record<string, string> = {
  'Eligibility - Incarcarated': 'Eligibility - Incarcerated',
};

const REASON_OPTIONS = INACTIVE_STATUS_REASONS.map((reasonOption) => ({
  value: reasonOption,
  label: REASON_LABELS[reasonOption] ?? reasonOption,
}));

const REAL_APPOINTMENT_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function normalizedStatus(status: string | null | undefined): string {
  return String(status ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function deviceStatusCode(status: MatchDevice['status']): number | null {
  if (status === undefined || status === null || status === '') return null;
  const named: Record<string, number> = {
    inactive: 0,
    active: 1,
    'new implant': 2,
    pending: 3,
    'entered in error': 4,
  };
  const text = String(status).trim().toLowerCase();
  if (text in named) return named[text];
  const numeric = Number(text);
  return Number.isFinite(numeric) ? numeric : null;
}

/** Active, new implant, and pending devices move to inactive with the patient. */
function shouldInactivateDevice(device: MatchDevice): boolean {
  const code = deviceStatusCode(device.status);
  return code === 1 || code === 2 || code === 3;
}

function isTerminalAppointment(status: string | null | undefined): boolean {
  const value = normalizedStatus(status);
  return value === 'completed' || value === 'complete' || value === 'cancelled' || value === 'canceled';
}

const PRESERVED_TASK_CODES = new Set([
  'transmission_tracking',
  'check_in',
  'report_upload',
  'report_upload_with_encounter',
]);

/** Report upload, check-in, and transmission tracking stay open when the patient is inactivated. */
function isPreservedTask(task: TaskResponseDto): boolean {
  return PRESERVED_TASK_CODES.has(task.taskType?.code ?? '');
}

/**
 * Candidate enrollment and the other admin tasks use the canceled outcome
 * Deactivated Patient. Billing and patient-support catalogs do not have that code,
 * so those cancels stay unstamped rather than failing.
 */
function inactivePatientCancelOutcome(
  task: TaskResponseDto,
): { outcomeCode: 'deactivated_patient' } | Record<string, never> {
  const workType = task.workType || task.taskType?.workType;
  if (workType !== 'admin') return {};
  return { outcomeCode: 'deactivated_patient' };
}

function isTransmissionTask(task: TaskResponseDto): boolean {
  return task.taskType?.code === 'transmission_tracking';
}

const OPEN_TASK_STATES = new Set(['open', 'in_progress', 'on_hold']);

export function DeactivatePatientModal({
  opened,
  onClose,
  patientId,
  patientName,
  devices = [],
}: DeactivatePatientModalProps) {
  const dispatch = useAppDispatch();
  const [reason, setReason] = useState<string | null>(null);
  const [explanation, setExplanation] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [updatePatient] = useUpdatePatientMutation();
  const [updateDevice] = useUpdateDeviceMutation();
  const [updateAppointment] = useUpdateAppointmentMutation();
  const [cancelTask] = useCancelTaskMutation();
  const [listTasks] = useLazyListTasksQuery();
  const [listAppointments] = useLazyListAppointmentsQuery();

  useEffect(() => {
    if (!opened) {
      setReason(null);
      setExplanation('');
    }
  }, [opened]);

  const isOther = reason === 'Other';
  const explanationText = explanation.trim();

  const handleSubmit = async () => {
    if (!reason || (isOther && !explanationText) || isSaving) return;
    setIsSaving(true);
    const problems: string[] = [];
    try {
      const openTasks = await loadOpenTasks(listTasks, patientId);
      const appointments = await loadAppointments(listAppointments, patientId);
      const protectedAppointmentIds = new Set(
        openTasks
          .filter((task) => isTransmissionTask(task) && task.appointmentId)
          .map((task) => task.appointmentId as string),
      );
      const tasksToCancel = openTasks.filter((task) => !isPreservedTask(task));

      await updatePatient({
        patient: {
          id: patientId,
          status: 'Inactive' as unknown as Patient['status'],
          statusReason: reason,
          ...(isOther ? { statusOtherReason: explanationText } : {}),
        },
      }).unwrap();

      for (const device of devices.filter(shouldInactivateDevice)) {
        if (!device.serialNumber?.trim() || !device.vendor) {
          problems.push(`${device.modelName || 'A device'} could not be set inactive.`);
          continue;
        }
        try {
          await updateDevice({
            patientId,
            reportId: undefined,
            id: device.id,
            vendor: device.vendor,
            serialNumber: device.serialNumber,
            modelNumber: device.modelNumber,
            modelName: device.modelName,
            type: device.type,
            // A numeric 0 is ignored by the device update. The name is what sets Inactive.
            status: 'Inactive',
          }).unwrap();
        } catch {
          problems.push(`${device.modelName || 'A device'} could not be set inactive.`);
        }
      }

      const seen = new Set<string>();
      for (const appointment of appointments) {
        if (!REAL_APPOINTMENT_ID.test(appointment.id) || seen.has(appointment.id)) continue;
        seen.add(appointment.id);
        if (isTerminalAppointment(appointment.status)) continue;
        if (protectedAppointmentIds.has(appointment.id)) continue;
        try {
          await updateAppointment({ id: appointment.id, status: 'cancelled' }).unwrap();
        } catch {
          problems.push('An open appointment could not be canceled.');
        }
      }

      for (const task of tasksToCancel) {
        try {
          await cancelTask({
            id: task.id,
            body: {
              expectedVersion: task.version,
              reason: 'Patient was set inactive.',
              ...inactivePatientCancelOutcome(task),
            },
          }).unwrap();
        } catch {
          problems.push(`${task.taskType?.name || 'An admin task'} could not be canceled.`);
        }
      }

      dispatch(
        patientsApiSlice.util.invalidateTags([
          { type: API_SLICE.Patient, id: patientId },
          { type: API_SLICE.Patient, id: `${patientId}-schedules` },
        ]),
      );

      notifications.show({
        title: 'Patient deactivated',
        message: problems.length
          ? `${patientName} is inactive. ${problems.length} related update${problems.length === 1 ? '' : 's'} did not finish.`
          : `${patientName} is inactive. Active and pending devices are inactive, open appointments are canceled, and open tasks are canceled.`,
        color: problems.length ? 'yellow' : 'teal',
      });
      onClose();
    } catch (error) {
      const data = (error as { data?: { message?: string; details?: { msg?: string }[] } })?.data;
      const detail = data?.details?.find((item) => item.msg)?.msg;
      notifications.show({
        title: 'Error',
        message: detail && detail !== 'Bad Request' ? detail : 'Failed to deactivate patient.',
        color: 'red',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CustomModal
      header={{ title: 'Deactivate Patient' }}
      modalProps={{ opened, onClose, size: 'md' }}
      secondaryButton={{ text: 'Cancel', onClick: onClose }}
      primaryButton={{
        text: 'Deactivate Patient',
        onClick: handleSubmit,
        isDisabled: !reason || (isOther && !explanationText),
        isLoading: isSaving,
      }}>
      <Stack gap="md" p="md">
        <Text size="sm" c="dimmed">
          Deactivating {patientName} sets the patient inactive. Active, new implant, and pending
          devices become inactive. Open appointments, including future scheduled ones, are
          canceled. Completed and canceled appointments stay as they are, and so does any
          appointment with an open transmission task. Open, in progress, and on hold tasks are
          canceled, except report upload, check-in, and transmission tracking.
        </Text>
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            Inactive reason <Text span c="red">*</Text>
          </Text>
          <Select
            placeholder="Select a reason..."
            data={REASON_OPTIONS}
            value={reason}
            onChange={setReason}
            searchable
            allowDeselect={false}
          />
        </Stack>
        {isOther && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Inactive explanation <Text span c="red">*</Text>
            </Text>
            <Textarea
              placeholder="Required when the reason is Other"
              minRows={3}
              autosize
              value={explanation}
              onChange={(e) => setExplanation(e.currentTarget.value)}
            />
          </Stack>
        )}
      </Stack>
    </CustomModal>
  );
}

async function loadAppointments(
  listAppointments: ReturnType<typeof useLazyListAppointmentsQuery>[0],
  patientId: string,
): Promise<{ id: string; status?: string | null }[]> {
  const all: { id: string; status?: string | null }[] = [];
  let page = 1;
  for (let attempt = 0; attempt < 20; attempt += 1) {
    const result = await listAppointments({ patientId, page, limit: 100 }).unwrap();
    all.push(...result.data);
    if (all.length >= result.total || result.data.length === 0) break;
    page += 1;
  }
  return all;
}

async function loadOpenTasks(
  listTasks: ReturnType<typeof useLazyListTasksQuery>[0],
  patientId: string,
): Promise<TaskResponseDto[]> {
  const all: TaskResponseDto[] = [];
  let skip = 0;
  for (let page = 0; page < 10; page += 1) {
    const result = await listTasks({
      patientId,
      businessStates: ['open', 'in_progress', 'on_hold'],
      limit: 100,
      skip,
    }).unwrap();
    all.push(...result.items.filter((task) => OPEN_TASK_STATES.has(task.businessState)));
    if (result.items.length < 100) break;
    skip += 100;
  }
  return all;
}
